import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Ujednolicony przełącznik (kółko + tor), do użycia zamiast kopiowania markupu
 * `.toggle-switch` po komponentach. Kolory kółka są odwrócone względem stanu —
 * jasne na niezaznaczonym / ciemne na zaznaczonym w jasnym motywie i odwrotnie
 * w ciemnym — inaczej kółko ginie na tle toru tego samego koloru.
 *
 * Działa jako ControlValueAccessor (formControlName / ngModel) i, dla użyć poza
 * formularzem, przez zwykłe [checked] + (checkedChange).
 */
@Component({
  selector: 'app-toggle-switch',
  template: `
    <label class="toggle-switch" [class.disabled]="disabled">
      <input
        type="checkbox"
        [checked]="checked"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel || null"
        (change)="onChange($event)"
        (blur)="propagateTouched()"
      />
      <span class="slider round"></span>
    </label>
  `,
  styleUrls: ['./toggle-switch.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ToggleSwitchComponent),
    multi: true
  }]
})
export class ToggleSwitchComponent implements ControlValueAccessor {

  @Input() checked = false;
  @Input() disabled = false;
  @Input() ariaLabel = '';

  @Output() checkedChange = new EventEmitter<boolean>();

  private propagateChange: (value: boolean) => void = () => {};
  propagateTouched: () => void = () => {};

  onChange(event: Event): void {
    this.checked = (event.target as HTMLInputElement).checked;
    this.propagateChange(this.checked);
    this.checkedChange.emit(this.checked);
  }

  writeValue(value: boolean): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
